import { 
  Car, 
  Waves, 
  Trees, 
  LayoutGrid, 
  Flame, 
  Wind, 
  Thermometer,
  FileText,
  Mountain,
  Anchor,
  Warehouse,
  Sun,
  Square,
  Shield,
  PawPrint,
  Wifi,
  Receipt,
  WashingMachine,
  CookingPot,
  Sofa,
  Droplets,
  Dumbbell,
  Building2,
  Home,
  Tv,
  Snowflake,
  Zap,
  Bath,
  Umbrella,
  Lock,
  Bed,
  Store,
  Bus,
  Users,
  Phone,
  Truck,
  Coffee,
  UtensilsCrossed,
  MapPin,
  Fence,
  Droplet
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';

export interface Feature {
  id: string;
  label: string;
  icon: LucideIcon;
}

export interface FeatureCategory {
  id: string;
  label: string;
  features: Feature[];
}

// Residential Sale Features
export const saleFeatureCategories: FeatureCategory[] = [
  {
    id: 'outdoor',
    label: 'Outdoor & Views',
    features: [
      { id: 'pool', label: 'Private Pool', icon: Waves },
      { id: 'communal-pool', label: 'Communal Pool', icon: Droplets },
      { id: 'garden', label: 'Garden', icon: Trees },
      { id: 'balcony', label: 'Balcony', icon: LayoutGrid },
      { id: 'terrace', label: 'Terrace / Veranda', icon: Umbrella },
      { id: 'rooftop', label: 'Rooftop Terrace', icon: Building2 },
      { id: 'sea-view', label: 'Sea View', icon: Anchor },
      { id: 'mountain-view', label: 'Mountain View', icon: Mountain },
      { id: 'city-view', label: 'City View', icon: Building2 },
      { id: 'bbq-area', label: 'BBQ Area', icon: Flame },
    ],
  },
  {
    id: 'climate',
    label: 'Climate & Energy',
    features: [
      { id: 'air-conditioning', label: 'A/C', icon: Snowflake },
      { id: 'central-heating', label: 'Central Heating', icon: Thermometer },
      { id: 'underfloor-heating', label: 'Underfloor Heating', icon: Flame },
      { id: 'fireplace', label: 'Fireplace', icon: Flame },
      { id: 'solar-panels', label: 'Solar Panels', icon: Sun },
      { id: 'solar-water-heater', label: 'Solar Water Heater', icon: Sun },
      { id: 'double-glazing', label: 'Double Glazing', icon: Square },
      { id: 'energy-class-a', label: 'Energy Class A+', icon: Zap },
      { id: 'heat-pump', label: 'Heat Pump', icon: Wind },
    ],
  },
  {
    id: 'building',
    label: 'Building & Interior',
    features: [
      { id: 'elevator', label: 'Elevator', icon: Building2 },
      { id: 'gym', label: 'Gym', icon: Dumbbell },
      { id: 'smart-home', label: 'Smart Home', icon: Home },
      { id: 'en-suite', label: 'En-suite Bathroom', icon: Bath },
      { id: 'guest-wc', label: 'Guest WC', icon: Bath },
      { id: 'maids-room', label: "Maid's Room", icon: Bed },
      { id: 'storage', label: 'Storage Room', icon: Warehouse },
      { id: 'laundry-room', label: 'Laundry Room', icon: WashingMachine },
      { id: 'walk-in-closet', label: 'Walk-in Closet', icon: Home },
      { id: 'jacuzzi', label: 'Jacuzzi / Hot Tub', icon: Droplets },
      { id: 'sauna', label: 'Sauna', icon: Thermometer },
    ],
  },
  {
    id: 'practical',
    label: 'Parking & Security',
    features: [
      { id: 'parking', label: 'Private Parking', icon: Car },
      { id: 'covered-parking', label: 'Covered Parking', icon: Car },
      { id: 'garage', label: 'Garage', icon: Warehouse },
      { id: 'security', label: 'Security System', icon: Shield },
      { id: 'cctv', label: 'CCTV Cameras', icon: Shield },
      { id: 'gated', label: 'Gated Community', icon: Lock },
      { id: 'concierge', label: 'Concierge', icon: Users },
      { id: 'title-deeds', label: 'Title Deeds', icon: FileText },
    ],
  },
  {
    id: 'furnished',
    label: 'Furnishing',
    features: [
      { id: 'fully-furnished', label: 'Fully Furnished', icon: Sofa },
      { id: 'partially-furnished', label: 'Partially Furnished', icon: Sofa },
      { id: 'white-goods', label: 'White Goods Included', icon: CookingPot },
    ],
  },
  {
    id: 'location',
    label: 'Location',
    features: [
      { id: 'near-beach', label: 'Near Beach', icon: Umbrella },
      { id: 'beachfront', label: 'Beachfront', icon: Anchor },
      { id: 'near-shops', label: 'Near Shops', icon: Store },
      { id: 'near-transport', label: 'Near Transport', icon: Bus },
      { id: 'near-schools', label: 'Near Schools', icon: Building2 },
      { id: 'quiet-area', label: 'Quiet Area', icon: MapPin },
      { id: 'tourist-area', label: 'Tourist Area', icon: MapPin },
    ],
  },
];

// Residential Rent Features
export const rentFeatureCategories: FeatureCategory[] = [
  {
    id: 'outdoor',
    label: 'Outdoor & Views',
    features: [
      { id: 'pool', label: 'Private Pool', icon: Waves },
      { id: 'communal-pool', label: 'Communal Pool', icon: Droplets },
      { id: 'garden', label: 'Garden', icon: Trees },
      { id: 'balcony', label: 'Balcony', icon: LayoutGrid },
      { id: 'terrace', label: 'Terrace / Veranda', icon: Umbrella },
      { id: 'sea-view', label: 'Sea View', icon: Anchor },
      { id: 'mountain-view', label: 'Mountain View', icon: Mountain },
      { id: 'city-view', label: 'City View', icon: Building2 },
      { id: 'bbq-area', label: 'BBQ Area', icon: Flame },
    ],
  },
  {
    id: 'climate',
    label: 'Climate Control',
    features: [
      { id: 'air-conditioning', label: 'A/C', icon: Snowflake },
      { id: 'central-heating', label: 'Central Heating', icon: Thermometer },
      { id: 'fireplace', label: 'Fireplace', icon: Flame },
      { id: 'heat-pump', label: 'Heat Pump', icon: Wind },
    ],
  },
  {
    id: 'utilities',
    label: 'Utilities Included',
    features: [
      { id: 'internet', label: 'Internet', icon: Wifi },
      { id: 'bills-included', label: 'Bills Included', icon: Receipt },
      { id: 'cable-tv', label: 'Cable TV', icon: Tv },
      { id: 'cleaning', label: 'Cleaning Service', icon: Home },
    ],
  },
  {
    id: 'appliances',
    label: 'Appliances',
    features: [
      { id: 'washing-machine', label: 'Washing Machine', icon: WashingMachine },
      { id: 'dryer', label: 'Dryer', icon: Wind },
      { id: 'dishwasher', label: 'Dishwasher', icon: CookingPot },
      { id: 'white-goods', label: 'White Goods', icon: CookingPot },
    ],
  },
  {
    id: 'building',
    label: 'Building',
    features: [
      { id: 'elevator', label: 'Elevator', icon: Building2 },
      { id: 'gym', label: 'Gym', icon: Dumbbell },
      { id: 'storage', label: 'Storage', icon: Warehouse },
      { id: 'jacuzzi', label: 'Jacuzzi / Hot Tub', icon: Droplets },
      { id: 'sauna', label: 'Sauna', icon: Thermometer },
    ],
  },
  {
    id: 'furnished',
    label: 'Furnishing',
    features: [
      { id: 'fully-furnished', label: 'Fully Furnished', icon: Sofa },
      { id: 'partially-furnished', label: 'Partially Furnished', icon: Sofa },
    ],
  },
  {
    id: 'practical',
    label: 'Practical',
    features: [
      { id: 'parking', label: 'Parking', icon: Car },
      { id: 'covered-parking', label: 'Covered Parking', icon: Car },
      { id: 'security', label: 'Security', icon: Shield },
      { id: 'pets-allowed', label: 'Pets Allowed', icon: PawPrint },
      { id: 'concierge', label: 'Concierge', icon: Users },
    ],
  },
  {
    id: 'location',
    label: 'Location',
    features: [
      { id: 'near-beach', label: 'Near Beach', icon: Umbrella },
      { id: 'near-shops', label: 'Near Shops', icon: Store },
      { id: 'near-transport', label: 'Near Transport', icon: Bus },
      { id: 'near-schools', label: 'Near Schools', icon: Building2 },
    ],
  },
];

// Commercial Sale Features
export const commercialSaleFeatureCategories: FeatureCategory[] = [
  {
    id: 'building',
    label: 'Building Features',
    features: [
      { id: 'elevator', label: 'Elevator', icon: Building2 },
      { id: 'air-conditioning', label: 'A/C System', icon: Snowflake },
      { id: 'central-heating', label: 'Central Heating', icon: Thermometer },
      { id: 'reception', label: 'Reception Area', icon: Users },
      { id: 'meeting-rooms', label: 'Meeting Rooms', icon: Users },
      { id: 'kitchen-area', label: 'Kitchen/Pantry', icon: Coffee },
    ],
  },
  {
    id: 'utilities',
    label: 'Utilities & Tech',
    features: [
      { id: 'high-speed-internet', label: 'Fiber Ready', icon: Wifi },
      { id: 'phone-lines', label: 'Phone Lines', icon: Phone },
      { id: 'backup-power', label: 'Backup Power', icon: Zap },
      { id: 'solar-panels', label: 'Solar Panels', icon: Sun },
    ],
  },
  {
    id: 'practical',
    label: 'Access & Security',
    features: [
      { id: 'parking', label: 'Parking Spaces', icon: Car },
      { id: 'loading-dock', label: 'Loading Dock', icon: Truck },
      { id: 'security', label: 'Security System', icon: Shield },
      { id: 'cctv', label: 'CCTV', icon: Shield },
      { id: '24h-access', label: '24h Access', icon: Lock },
      { id: 'storage', label: 'Storage Area', icon: Warehouse },
    ],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    features: [
      { id: 'shop-front', label: 'Shop Front', icon: Store },
      { id: 'high-footfall', label: 'High Footfall', icon: Users },
      { id: 'corner-location', label: 'Corner Location', icon: MapPin },
      { id: 'title-deeds', label: 'Title Deeds', icon: FileText },
    ],
  },
];

// Commercial Rent Features
export const commercialRentFeatureCategories: FeatureCategory[] = [
  {
    id: 'building',
    label: 'Building Features',
    features: [
      { id: 'elevator', label: 'Elevator', icon: Building2 },
      { id: 'air-conditioning', label: 'A/C System', icon: Snowflake },
      { id: 'central-heating', label: 'Central Heating', icon: Thermometer },
      { id: 'reception', label: 'Reception Area', icon: Users },
      { id: 'kitchen-area', label: 'Kitchen/Pantry', icon: Coffee },
    ],
  },
  {
    id: 'utilities',
    label: 'Utilities Included',
    features: [
      { id: 'internet', label: 'Internet Included', icon: Wifi },
      { id: 'bills-included', label: 'Bills Included', icon: Receipt },
      { id: 'cleaning', label: 'Cleaning Service', icon: Home },
    ],
  },
  {
    id: 'practical',
    label: 'Access & Security',
    features: [
      { id: 'parking', label: 'Parking Spaces', icon: Car },
      { id: 'loading-dock', label: 'Loading Dock', icon: Truck },
      { id: 'security', label: 'Security System', icon: Shield },
      { id: '24h-access', label: '24h Access', icon: Lock },
    ],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    features: [
      { id: 'shop-front', label: 'Shop Front', icon: Store },
      { id: 'high-footfall', label: 'High Footfall', icon: Users },
      { id: 'signage-allowed', label: 'Signage Allowed', icon: Store },
    ],
  },
];

// Land Features
export const landFeatureCategories: FeatureCategory[] = [
  {
    id: 'land',
    label: 'Land Features',
    features: [
      { id: 'building-permit', label: 'Building Permit', icon: FileText },
      { id: 'road-access', label: 'Road Access', icon: Car },
      { id: 'sea-view', label: 'Sea View', icon: Anchor },
      { id: 'mountain-view', label: 'Mountain View', icon: Mountain },
      { id: 'flat-terrain', label: 'Flat Terrain', icon: Square },
      { id: 'fenced', label: 'Fenced', icon: Fence },
    ],
  },
  {
    id: 'utilities',
    label: 'Utilities Available',
    features: [
      { id: 'electricity', label: 'Electricity', icon: Zap },
      { id: 'water', label: 'Water Supply', icon: Droplet },
      { id: 'sewage', label: 'Sewage Connection', icon: Home },
      { id: 'phone-lines', label: 'Phone Lines', icon: Phone },
    ],
  },
  {
    id: 'zoning',
    label: 'Zoning & Legal',
    features: [
      { id: 'residential-zone', label: 'Residential Zone', icon: Home },
      { id: 'commercial-zone', label: 'Commercial Zone', icon: Building2 },
      { id: 'title-deeds', label: 'Title Deeds', icon: FileText },
      { id: 'no-restrictions', label: 'No Building Restrictions', icon: Square },
    ],
  },
  {
    id: 'location',
    label: 'Location',
    features: [
      { id: 'near-beach', label: 'Near Beach', icon: Umbrella },
      { id: 'near-highway', label: 'Near Highway', icon: Car },
      { id: 'quiet-area', label: 'Quiet Area', icon: MapPin },
    ],
  },
];

export const furnishedOptions = [
  { value: 'fully', label: 'Fully Furnished', icon: Sofa },
  { value: 'partially', label: 'Partially Furnished', icon: Sofa },
  { value: 'unfurnished', label: 'Unfurnished', icon: Square },
] as const;

// Helper to get features based on category and listing type
export function getFeatureCategories(
  propertyCategory: 'residential' | 'commercial' | 'land',
  listingType: 'sale' | 'rent'
): FeatureCategory[] {
  if (propertyCategory === 'land') {
    return landFeatureCategories;
  }
  if (propertyCategory === 'commercial') {
    return listingType === 'rent' ? commercialRentFeatureCategories : commercialSaleFeatureCategories;
  }
  return listingType === 'rent' ? rentFeatureCategories : saleFeatureCategories;
}

// Feature detection patterns - maps feature IDs to keywords/phrases to match
// Extended with more variations and common real estate terminology
const featureKeywords: Record<string, string[]> = {
  // Outdoor & Views
  'pool': ['pool', 'swimming pool', 'πισίνα', 'private pool', 'infinity pool', 'heated pool'],
  'communal-pool': ['communal pool', 'shared pool', 'κοινόχρηστη πισίνα', 'common pool', 'residents pool'],
  'garden': ['garden', 'κήπος', 'planted garden', 'landscaped', 'private garden', 'mature garden', 'lawn'],
  'balcony': ['balcony', 'μπαλκόνι', 'balconies', 'covered balcony', 'large balcony'],
  'terrace': ['terrace', 'veranda', 'βεράντα', 'patio', 'covered terrace', 'sun terrace', 'private terrace'],
  'rooftop': ['rooftop', 'roof terrace', 'roof garden', 'rooftop terrace', 'roof deck'],
  'sea-view': ['sea view', 'ocean view', 'θέα θάλασσα', 'θαλασσινή θέα', 'seaview', 'sea views', 'views of the sea', 'mediterranean view', 'coastal view'],
  'mountain-view': ['mountain view', 'θέα βουνό', 'mountainview', 'mountain views', 'troodos view', 'views of the mountains'],
  'city-view': ['city view', 'cityview', 'urban view', 'town view', 'panoramic view'],
  'bbq-area': ['bbq', 'barbecue', 'barbeque', 'grill area', 'outdoor kitchen', 'ψησταριά'],
  'beachfront': ['beachfront', 'beach front', 'on the beach', 'seafront', 'first line', 'front line'],
  
  // Climate & Energy
  'air-conditioning': ['a/c', 'air conditioning', 'air-conditioning', 'κλιματισμός', 'ac unit', 'aircon', 'air con', 'split unit', 'cooling', 'air conditioner', 'climate control', 'ac'],
  'central-heating': ['central heating', 'κεντρική θέρμανση', 'heating system', 'radiators', 'oil heating', 'gas heating'],
  'underfloor-heating': ['underfloor heating', 'floor heating', 'ενδοδαπέδια', 'heated floors', 'radiant floor'],
  'fireplace': ['fireplace', 'τζάκι', 'fire place', 'wood burning', 'open fire', 'log burner'],
  'solar-panels': ['solar panel', 'ηλιακός', 'photovoltaic', 'pv system', 'pv panels', 'solar energy', 'renewable'],
  'solar-water-heater': ['solar water', 'solar heater', 'ηλιακός θερμοσίφωνας', 'solar hot water'],
  'double-glazing': ['double glazing', 'double-glazed', 'διπλά τζάμια', 'double glass', 'insulated windows', 'thermal windows'],
  'energy-class-a': ['energy class a', 'energy rating a', 'ενεργειακή κλάση α', 'energy efficient', 'a+ rating', 'high efficiency'],
  'heat-pump': ['heat pump', 'αντλία θερμότητας', 'air source', 'ground source'],
  
  // Building & Interior
  'elevator': ['elevator', 'lift', 'ανελκυστήρας', 'ασανσέρ', 'with lift', 'has elevator'],
  'gym': ['gym', 'fitness', 'γυμναστήριο', 'fitness center', 'fitness centre', 'exercise room', 'workout'],
  'smart-home': ['smart home', 'home automation', 'έξυπνο σπίτι', 'domotic', 'smart system', 'automated'],
  'en-suite': ['en-suite', 'ensuite', 'en suite', 'master bathroom', 'private bathroom', 'attached bathroom'],
  'guest-wc': ['guest wc', 'guest toilet', 'powder room', 'wc', 'half bath', 'extra wc'],
  'maids-room': ['maid\'s room', 'maids room', 'δωμάτιο υπηρεσίας', 'staff room', 'helper room', 'domestic room', 'service room'],
  'storage': ['storage room', 'storage space', 'αποθήκη', 'storeroom', 'utility room', 'store room', 'storage area'],
  'laundry-room': ['laundry room', 'laundry area', 'utility room', 'washing room'],
  'walk-in-closet': ['walk-in closet', 'walk in closet', 'walk-in wardrobe', 'dressing room', 'built-in wardrobes', 'fitted wardrobes'],
  'jacuzzi': ['jacuzzi', 'hot tub', 'spa bath', 'whirlpool', 'υδρομασάζ', 'jetted tub'],
  'sauna': ['sauna', 'steam room', 'σάουνα', 'steam bath'],
  
  // Parking & Security
  'parking': ['parking', 'πάρκινγκ', 'car space', 'parking space', 'car park', 'parking spot', 'parking area', 'private parking'],
  'covered-parking': ['covered parking', 'στεγασμένη θέση', 'covered car', 'underground parking', 'basement parking', 'indoor parking'],
  'garage': ['garage', 'γκαράζ', 'car garage', 'private garage', 'double garage'],
  'security': ['security system', 'alarm', 'συναγερμός', 'security alarm', 'burglar alarm', 'intruder alarm', 'security'],
  'cctv': ['cctv', 'camera', 'surveillance', 'κάμερες', 'video surveillance', 'security cameras'],
  'gated': ['gated community', 'gated complex', 'περιφραγμένο συγκρότημα', 'secure complex', 'gated development', 'enclosed complex'],
  'concierge': ['concierge', 'doorman', 'porter', 'reception', '24 hour reception', 'lobby attendant'],
  'title-deeds': ['title deed', 'title deeds', 'τίτλοι ιδιοκτησίας', 'separate deeds', 'full deeds', 'freehold'],
  
  // Furnishing
  'fully-furnished': ['fully furnished', 'fully-furnished', 'furnished', 'επιπλωμένο', 'complete furnishing', 'turn key', 'turnkey', 'move-in ready'],
  'partially-furnished': ['partially furnished', 'semi-furnished', 'part furnished', 'μερικώς επιπλωμένο', 'some furniture'],
  'white-goods': ['white goods', 'appliances included', 'kitchen appliances', 'fitted kitchen', 'equipped kitchen', 'ηλεκτρικές συσκευές'],
  
  // Location
  'near-beach': ['near beach', 'close to beach', 'walking distance to beach', 'κοντά στη θάλασσα', 'minutes from beach', 'beach nearby', 'near the sea'],
  'near-shops': ['near shops', 'close to shops', 'walking distance to shops', 'near amenities', 'close to amenities', 'near supermarket', 'shopping nearby'],
  'near-transport': ['near transport', 'public transport', 'bus stop', 'metro station', 'transport links', 'well connected'],
  'near-schools': ['near school', 'close to school', 'walking distance to school', 'near university', 'near college', 'schools nearby', 'school district'],
  'quiet-area': ['quiet area', 'peaceful', 'ήσυχη περιοχή', 'tranquil', 'residential area', 'quiet neighborhood', 'quiet street', 'cul-de-sac'],
  'tourist-area': ['tourist area', 'popular area', 'holiday area', 'resort area', 'touristic'],
  
  // Rent specific
  'internet': ['internet', 'wifi', 'wi-fi', 'broadband', 'ίντερνετ', 'fiber internet', 'high speed internet'],
  'bills-included': ['bills included', 'utilities included', 'all inclusive', 'all bills', 'inclusive of bills'],
  'cable-tv': ['cable tv', 'satellite tv', 'tv included', 'television', 'tv channels'],
  'cleaning': ['cleaning', 'cleaning service', 'maid service', 'housekeeping', 'weekly cleaning'],
  'washing-machine': ['washing machine', 'washer', 'πλυντήριο', 'laundry facilities'],
  'dryer': ['dryer', 'tumble dryer', 'στεγνωτήριο', 'clothes dryer'],
  'dishwasher': ['dishwasher', 'πλυντήριο πιάτων', 'dish washer'],
  'pets-allowed': ['pets allowed', 'pet friendly', 'κατοικίδια', 'pets welcome', 'animals allowed', 'dogs allowed', 'cats allowed'],
  
  // Commercial
  'reception': ['reception', 'reception area', 'υποδοχή', 'lobby', 'entrance hall', 'foyer'],
  'meeting-rooms': ['meeting room', 'conference room', 'boardroom', 'αίθουσα συσκέψεων', 'meeting space'],
  'kitchen-area': ['kitchen area', 'kitchenette', 'pantry', 'staff kitchen', 'break room', 'tea point'],
  'high-speed-internet': ['fiber', 'fibre', 'high speed internet', 'οπτική ίνα', 'gigabit', 'fiber optic'],
  'phone-lines': ['phone line', 'telephone', 'τηλεφωνική γραμμή', 'landline', 'phone system'],
  'backup-power': ['backup power', 'generator', 'ups', 'γεννήτρια', 'emergency power', 'power backup'],
  'loading-dock': ['loading dock', 'loading bay', 'ράμπα φόρτωσης', 'goods entrance', 'delivery access'],
  '24h-access': ['24h access', '24 hour access', '24-hour', 'πρόσβαση 24 ώρες', 'round the clock', 'always accessible'],
  'shop-front': ['shop front', 'shopfront', 'storefront', 'βιτρίνα', 'display window', 'street frontage'],
  'high-footfall': ['high footfall', 'busy area', 'high traffic', 'πολυσύχναστη', 'busy street', 'prime location', 'main street'],
  'corner-location': ['corner', 'γωνιακό', 'corner unit', 'corner position', 'corner plot'],
  'signage-allowed': ['signage', 'advertising allowed', 'επιγραφή', 'fascia', 'advertising space'],
  
  // Land
  'building-permit': ['building permit', 'άδεια οικοδομής', 'planning permission', 'approved plans', 'with permit'],
  'road-access': ['road access', 'paved road', 'asphalt road', 'ασφαλτοστρωμένος', 'road frontage', 'main road access'],
  'flat-terrain': ['flat terrain', 'flat land', 'level', 'επίπεδο', 'level ground', 'flat plot'],
  'fenced': ['fenced', 'περιφραγμένο', 'enclosed', 'boundary wall', 'walled'],
  'electricity': ['electricity', 'ρεύμα', 'power supply', 'electric connection', 'mains electricity'],
  'water': ['water supply', 'water connection', 'νερό', 'ύδρευση', 'mains water', 'water mains'],
  'sewage': ['sewage', 'drainage', 'αποχέτευση', 'sewer connection', 'mains drainage'],
  'residential-zone': ['residential zone', 'κατοικία ζώνη', 'housing zone', 'residential area'],
  'commercial-zone': ['commercial zone', 'εμπορική ζώνη', 'business zone', 'commercial area'],
  'no-restrictions': ['no restrictions', 'χωρίς περιορισμούς', 'unrestricted', 'no building restrictions'],
  'near-highway': ['near highway', 'motorway', 'κοντά σε αυτοκινητόδρομο', 'highway access', 'motorway access', 'main road'],
};

// Map from common variations/AI outputs to our feature IDs
const featureNormalizationMap: Record<string, string> = {
  // Pool variations
  'pool': 'pool',
  'private pool': 'pool',
  'swimming pool': 'pool',
  'infinity pool': 'pool',
  'communal pool': 'communal-pool',
  'shared pool': 'communal-pool',
  'common pool': 'communal-pool',
  
  // Views
  'sea view': 'sea-view',
  'seaview': 'sea-view',
  'sea views': 'sea-view',
  'ocean view': 'sea-view',
  'mountain view': 'mountain-view',
  'mountain views': 'mountain-view',
  'city view': 'city-view',
  'panoramic view': 'city-view',
  
  // Climate
  'air conditioning': 'air-conditioning',
  'a/c': 'air-conditioning',
  'ac': 'air-conditioning',
  'aircon': 'air-conditioning',
  'split unit': 'air-conditioning',
  'central heating': 'central-heating',
  'underfloor heating': 'underfloor-heating',
  'floor heating': 'underfloor-heating',
  'fireplace': 'fireplace',
  'solar panels': 'solar-panels',
  'solar water heater': 'solar-water-heater',
  'double glazing': 'double-glazing',
  'heat pump': 'heat-pump',
  
  // Building
  'elevator': 'elevator',
  'lift': 'elevator',
  'gym': 'gym',
  'fitness': 'gym',
  'fitness center': 'gym',
  'smart home': 'smart-home',
  'home automation': 'smart-home',
  'en-suite bathroom': 'en-suite',
  'en-suite': 'en-suite',
  'ensuite': 'en-suite',
  'guest wc': 'guest-wc',
  'guest toilet': 'guest-wc',
  'maids room': 'maids-room',
  "maid's room": 'maids-room',
  'storage room': 'storage',
  'storage': 'storage',
  'laundry room': 'laundry-room',
  'walk-in closet': 'walk-in-closet',
  'jacuzzi': 'jacuzzi',
  'hot tub': 'jacuzzi',
  'sauna': 'sauna',
  
  // Outdoor
  'garden': 'garden',
  'private garden': 'garden',
  'balcony': 'balcony',
  'terrace': 'terrace',
  'veranda': 'terrace',
  'patio': 'terrace',
  'rooftop terrace': 'rooftop',
  'roof terrace': 'rooftop',
  'bbq area': 'bbq-area',
  'bbq': 'bbq-area',
  'beachfront': 'beachfront',
  'beach front': 'beachfront',
  
  // Parking & Security
  'parking': 'parking',
  'private parking': 'parking',
  'parking space': 'parking',
  'covered parking': 'covered-parking',
  'underground parking': 'covered-parking',
  'basement parking': 'covered-parking',
  'garage': 'garage',
  'security system': 'security',
  'security': 'security',
  'alarm': 'security',
  'cctv': 'cctv',
  'security cameras': 'cctv',
  'gated community': 'gated',
  'gated': 'gated',
  'concierge': 'concierge',
  'title deeds': 'title-deeds',
  
  // Furnishing
  'fully furnished': 'fully-furnished',
  'furnished': 'fully-furnished',
  'partially furnished': 'partially-furnished',
  'semi-furnished': 'partially-furnished',
  'white goods': 'white-goods',
  'appliances': 'white-goods',
  
  // Location
  'near beach': 'near-beach',
  'near the beach': 'near-beach',
  'close to beach': 'near-beach',
  'near shops': 'near-shops',
  'near amenities': 'near-shops',
  'near transport': 'near-transport',
  'public transport': 'near-transport',
  'near schools': 'near-schools',
  'quiet area': 'quiet-area',
  'tourist area': 'tourist-area',
  
  // Utilities (rent)
  'internet': 'internet',
  'wifi': 'internet',
  'wi-fi': 'internet',
  'bills included': 'bills-included',
  'utilities included': 'bills-included',
  'cable tv': 'cable-tv',
  'cleaning service': 'cleaning',
  'washing machine': 'washing-machine',
  'dryer': 'dryer',
  'dishwasher': 'dishwasher',
  'pets allowed': 'pets-allowed',
  'pet friendly': 'pets-allowed',
  
  // Commercial
  'reception area': 'reception',
  'reception': 'reception',
  'meeting rooms': 'meeting-rooms',
  'conference room': 'meeting-rooms',
  'kitchen area': 'kitchen-area',
  'kitchenette': 'kitchen-area',
  'fiber internet': 'high-speed-internet',
  'high speed internet': 'high-speed-internet',
  'backup power': 'backup-power',
  'generator': 'backup-power',
  'loading dock': 'loading-dock',
  '24h access': '24h-access',
  '24 hour access': '24h-access',
  'shop front': 'shop-front',
  'storefront': 'shop-front',
  'high footfall': 'high-footfall',
  'corner location': 'corner-location',
  'corner': 'corner-location',
  
  // Land
  'building permit': 'building-permit',
  'road access': 'road-access',
  'flat terrain': 'flat-terrain',
  'fenced': 'fenced',
  'electricity': 'electricity',
  'water supply': 'water',
  'water': 'water',
  'sewage': 'sewage',
  'residential zone': 'residential-zone',
  'commercial zone': 'commercial-zone',
  'near highway': 'near-highway',
};

/**
 * Normalize AI-extracted features to standard feature IDs
 * @param aiFeatures Array of features extracted by AI (can be varied formats)
 * @param category Property category
 * @param listingType Listing type
 * @returns Array of normalized feature IDs
 */
export function normalizeFeatures(
  aiFeatures: string[],
  category: 'residential' | 'commercial' | 'land' = 'residential',
  listingType: 'sale' | 'rent' = 'sale'
): string[] {
  if (!aiFeatures || !Array.isArray(aiFeatures)) return [];
  
  const validCategories = getFeatureCategories(category, listingType);
  const validFeatureIds = new Set(
    validCategories.flatMap(cat => cat.features.map(f => f.id))
  );
  
  const normalizedFeatures: string[] = [];
  
  for (const feature of aiFeatures) {
    const lowerFeature = feature.toLowerCase().trim();
    
    // First try direct normalization map
    if (featureNormalizationMap[lowerFeature]) {
      const normalizedId = featureNormalizationMap[lowerFeature];
      if (validFeatureIds.has(normalizedId) && !normalizedFeatures.includes(normalizedId)) {
        normalizedFeatures.push(normalizedId);
      }
      continue;
    }
    
    // If already a valid ID, use it
    if (validFeatureIds.has(lowerFeature) && !normalizedFeatures.includes(lowerFeature)) {
      normalizedFeatures.push(lowerFeature);
      continue;
    }
    
    // Try fuzzy matching with keywords
    for (const [featureId, keywords] of Object.entries(featureKeywords)) {
      if (!validFeatureIds.has(featureId)) continue;
      
      for (const keyword of keywords) {
        if (lowerFeature.includes(keyword.toLowerCase()) || keyword.toLowerCase().includes(lowerFeature)) {
          if (!normalizedFeatures.includes(featureId)) {
            normalizedFeatures.push(featureId);
          }
          break;
        }
      }
    }
  }
  
  return normalizedFeatures;
}

/**
 * Detect features from listing description text
 * @param description The listing description text
 * @param category Property category (residential, commercial, land)
 * @param listingType Listing type (sale, rent)
 * @returns Array of detected feature IDs
 */
export function detectFeaturesFromDescription(
  description: string,
  category: 'residential' | 'commercial' | 'land' = 'residential',
  listingType: 'sale' | 'rent' = 'sale'
): string[] {
  if (!description) return [];
  
  const lowerDescription = description.toLowerCase();
  const detectedFeatures: string[] = [];
  
  // Get valid features for this category/type
  const validCategories = getFeatureCategories(category, listingType);
  const validFeatureIds = new Set(
    validCategories.flatMap(cat => cat.features.map(f => f.id))
  );
  
  // Check each feature's keywords against the description
  for (const [featureId, keywords] of Object.entries(featureKeywords)) {
    // Only detect features that are valid for this property type
    if (!validFeatureIds.has(featureId)) continue;
    
    for (const keyword of keywords) {
      if (lowerDescription.includes(keyword.toLowerCase())) {
        if (!detectedFeatures.includes(featureId)) {
          detectedFeatures.push(featureId);
        }
        break; // Found a match for this feature, move to next
      }
    }
  }
  
  return detectedFeatures;
}